import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { MinimalInput } from '@/components/ui/MinimalInput'
import { AlertCircle, Loader2, Mail } from 'lucide-react'

const theseusLogo = '/theseus-logo.png'

export function ForgotPasswordForm() {
  const { resetPassword, user } = useAuth()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  if (user) {
    return <Navigate to="/" replace />
  }

  const canSubmit = email.trim() !== ''

  const handleSubmit = async () => {
    setError(null)

    if (!email) {
      setError('Please enter your email address')
      return
    }

    setSubmitting(true)

    try {
      await resetPassword(email)
      setSuccess(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send reset email'
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {/* Logo - top left */}
        <a
          href="https://theseus.us"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
        >
          <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
        </a>

        {/* Centered content */}
        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-md wizard-fade-in">
            <div className="mb-10">
              <h1 className="text-2xl text-foreground">
                Check your email
              </h1>
              <p className="text-muted-foreground mt-2">
                We sent a reset link to <span className="text-foreground">{email}</span>
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Click the link in your email to reset your password. The link expires in 1 hour.</span>
              </div>

              <div className="pt-4">
                <Link
                  to="/login"
                  className="inline-block w-full px-6 py-2.5 rounded-md text-sm font-medium text-center transition-all
                    bg-secondary text-secondary-foreground hover:bg-secondary/80"
                >
                  Back to sign in
                </Link>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Didn't receive it?{' '}
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="text-primary hover:underline"
                >
                  Try again
                </button>
              </p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Logo - top left */}
      <a
        href="https://theseus.us"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
      >
        <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
      </a>

      {/* Centered content */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-10 wizard-fade-in">
            <h1 className="text-2xl text-foreground">
              Reset your password
            </h1>
            <p className="text-muted-foreground mt-2">
              Enter your email and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
            <div style={{ animationDelay: '50ms' }}>
              <MinimalInput
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onEnterPress={canSubmit ? handleSubmit : undefined}
                required
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive wizard-fade-in">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full px-6 py-2.5 rounded-md text-sm font-medium transition-all
                  bg-primary text-primary-foreground hover:bg-primary/90
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <>
                    Send reset link
                    <span className="text-primary-foreground/60 ml-2 text-xs">↵</span>
                  </>
                )}
              </button>
            </div>

            {/* Back to Login Link */}
            <p className="text-center text-sm text-muted-foreground pt-2">
              Remember your password?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </form>

          {/* Keyboard hint */}
          <div className="mt-8 text-center text-[10px] text-muted-foreground/40">
            <span className="px-1.5 py-0.5 bg-secondary/50 rounded">Enter</span> to continue
          </div>
        </div>
      </main>
    </div>
  )
}
